import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startBankOnb, submitBankOnb } from "./services/service";
import { useAppContext } from "utils/context";
import {
  convertOnbData,
  handleActionButtonMetrix,
} from "./services/data.transformers";
import Header from "./components/stages/header";
import {
  MetrixEvents,
  WebengageGeneralButtonEvent,
  handleClickMetrixEvents,
} from "containers/services/events.data";
import { getUserData } from "containers/services/data.transformers";
import { digitsFaToEn } from "@persian-tools/persian-tools";
import { useSelector } from "react-redux";
import LimitAmountDrawer from "./components/limitAmount/drawer";
import OnbSteps from "./components/pages/steps";
import OnbStages from "./components/stages/stages";
import PageLayout from "./components/layout/layout";
import "./index.scss";

const CardOnboarding = () => {
  const { instance, metrixInstance } = useAppContext();
  const navigate = useNavigate();
  const nationalCodeRef = useRef();
  const [data, setData] = useState();
  const [steps, setSteps] = useState(false);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const profile = useSelector((state) => state?.profileReducer?.userData);

  const handleCurrentStage = (res) =>
    res?.stages?.find((item) => item.key === res?.current_stage) ?? {};

  const handleStages = (response) => {
    const { status } = handleCurrentStage(response);
    const isFinalize = `${response.current_stage}`.toLowerCase() === "finalize";
    if (status === "FAILED") return setSteps(false);

    if (isFinalize) {
      setSteps(false);
      startOnb();
      return;
    }
    return setCurrent(current + 1);
  };

  const handleMetrix = (user, message) => {
    const attrs = [
      { key: "user_id", value: user?.sub },
      {
        key: "message",
        value: message,
      },
    ];
    MetrixEvents(metrixInstance, "njmhn", attrs);
  };

  const handleWebengageEvent = () => {
    return (
      current === 0 &&
      WebengageGeneralButtonEvent("bank-button-start", "", "button", {
        Type: "Bank",
        "Object id": "middleeast",
      })
    );
  };

  const startOnb = useCallback(async () => {
    setLoading(true);
    const params = { type: "BANK", ds_code: "MIDDLE_EAST" };
    const user = await getUserData();
    const response = await startBankOnb(instance, params);
    if (response?.data) {
      handleMetrix(
        user,
        response?.data?.stages?.filter((item) => item.status === "FAILED")[0]
          ?.message || ""
      );
      setData(response?.data);
    } else if (response?.response?.data?.detail === "LIMIT_AMOUNT") {
      setOpen(true);
    }
    setLoading(false);
  }, [instance]);

  useEffect(() => {
    startOnb();
  }, []);

  const submitOnb = async () => {
    setLoading(true);
    const response = await submitBankOnb(instance, data);
    if (response) {
      setData(response);
      handleStages(response);
    }
    setLoading(false);
  };

  useEffect(() => {
    setCurrent(handleCurrentStage(data)?.sort);
  }, [data]);

  useMemo(async () => {
    open === true &&
      (await handleClickMetrixEvents(
        metrixInstance,
        "bank-limit-amount-modal"
      ));
  }, [open]);

  const handleStagesNavigate = async () => {
    handleWebengageEvent();
    await handleActionButtonMetrix(current, metrixInstance, data);
    if (
      ["finalize", "message"].includes(`${data?.current_stage}`.toLowerCase())
    )
      return navigate("/card");

    return setSteps(true);
  };

  const handleStep = (value) => {
    setSteps(value);
    startOnb();
  };

  const onPrev = () => {
    setCurrent(current - 1);
  };

  const handleSpecificData = (key, values) => {
    setData(convertOnbData(data, key, values, current));
  };

  const handleDrawer = async (value) => {
    setOpen(value);
    value === false && navigate("/card");
  };

  const handleCardColor = (values) => {
    return data?.stages
      ?.filter((i) => i.key === "CARD")[0]
      ?.page_info?.filter(
        (i) => i.component_name === values?.card_name.toLowerCase()
      )[0]
      ?.infos?.filter((i) => i.label === "color_name")[0]?.value;
  };

  const handleWebengageEvents = (name) => {
    WebengageGeneralButtonEvent(name, "", "button", {
      Type: "Bank",
      "Object id": "middleeast",
    });
  };

  const validator = (_, value) => {
    const newValue = digitsFaToEn(`${value}`);
    return newValue.match(/^\d[a-zA-Z]\d{8}$/) ||
      (newValue !== profile?.national_code && newValue.match(/^\d{10}$/))
      ? Promise.resolve()
      : Promise.reject(
        `سریال کارت ملی یا کد رهگیری رسید را به درستی وارد فرمایید.`
      );
  };

  const onFinishBaseInfo = async (values) => {
    await handleClickMetrixEvents(
      metrixInstance,
      "bank-confirm-information-confirmation"
    );
    handleWebengageEvents("bank-button-verification");
    handleSpecificData("national_serial", values);
    await submitOnb();
  };

  const onFinishCard = async (values) => {
    await handleClickMetrixEvents(metrixInstance, "bank-select-card-color", [
      { key: "card_color", value: handleCardColor(values) },
    ]);
    handleWebengageEvents("bank-button-card-select");
    handleSpecificData("card_name", values);
    await submitOnb();
  };

  const onFinishVideo = async (values) => {
    await handleClickMetrixEvents(
      metrixInstance,
      "bank-video-verification-submit"
    );
    handleWebengageEvents("bank-button-video-review");
    handleSpecificData("video_text", values);
    await submitOnb();
  };

  const onFinishAddress = async (values) => {
    handleWebengageEvents("bank-button-address");
    handleSpecificData("address_id", values);
    await submitOnb();
  };

  const goBack = () => {
    if (current === 0) return handleStep(false);
    if (
      ["CARD", "VIDEO", "ADDRESS"].includes(
        `${data?.current_stage}`.toUpperCase()
      )
    )
      onPrev();
  };

  const onCloseOnboarding = () => {
    navigate("/card");
  };

  return (
    <PageLayout
      current={current}
      data={data}
      steps={steps}
      goBack={goBack}
      onCloseOnboarding={onCloseOnboarding}
    >
      {!steps ? (
        <>
          <Header />
          <OnbStages
            current={current}
            data={data}
            loading={loading}
            onClick={handleStagesNavigate}
          />
        </>
      ) : (
        <OnbSteps
          data={data}
          current={current}
          onFinishAddress={onFinishAddress}
          onFinishBaseInfo={onFinishBaseInfo}
          onFinishCard={onFinishCard}
          onFinishVideo={onFinishVideo}
          loading={loading}
          formRef={nationalCodeRef}
          validator={validator}
        />
      )}
      <LimitAmountDrawer open={open} handleDrawer={handleDrawer} />
    </PageLayout>
  );
};
export default CardOnboarding;
